/**
 * Team Capacity view (Operations, DWA Internal).
 *
 * Per-person utilization derived ONLY from the Teamwork time-entry
 * snapshot. "Capacity" = a person's available hours in the window
 * (workdays × 7hrs). "Utilization" = logged / capacity.
 *
 * Sections:
 *  1. Header — period selector (Week/Month), refresh, snapshot freshness
 *  2. Team KPI cards — headcount, logged, capacity, utilization
 *  3. Per-person table — logged vs capacity with a utilization bar
 *  4. Trend chart — team utilization across the trailing periods
 *
 * Theme is passed in (same ScorecardTheme the Creative Scorecard uses)
 * so the two DWA Internal dashboards render as one visual system.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  fetchTeamCapacity,
  fetchTeamCapacityForecast,
  type TeamCapacityResponse,
  type TeamCapacityPeriod,
  type TeamCapacityPerson,
  type TeamCapacityForecast,
  type ForecastGranularity,
  type ForecastSource,
} from '../services/dashboardApi';
import type { ScorecardTheme } from './ScorecardView';

export interface TeamCapacityProps {
  theme: ScorecardTheme;
}

// ─── Formatters ───────────────────────────────────────────────────────────

function fmt(n: number | null | undefined, digits = 0): string {
  if (n == null) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

function pct(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${(n * 100).toFixed(0)}%`;
}

function shortLabel(p: TeamCapacityPeriod, period: 'week' | 'month'): string {
  try {
    const d = new Date(p.start);
    if (period === 'week') {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US', { month: 'short' });
  } catch {
    return p.label;
  }
}

/**
 * Utilization → color. Under-target reads amber, healthy (roughly full)
 * green, over-capacity red (a signal the person is stretched past their
 * available hours, not a gold star).
 */
function utilizationColor(u: number, theme: ScorecardTheme): string {
  if (u > 1.05) return theme.negative;      // over capacity
  if (u >= 0.85) return theme.positive;      // healthy
  if (u >= 0.6) return '#f59e0b';            // amber — underloaded
  return theme.textMuted;                    // very low
}

// ─── Sub-components ───────────────────────────────────────────────────────

const MetricCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  theme: ScorecardTheme;
}> = ({ label, value, sub, theme }) => (
  <div style={{
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    boxShadow: theme.cardShadow,
    borderRadius: 4,
    padding: '1.25rem 1.5rem',
  }}>
    <p style={{
      fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
      textTransform: 'uppercase', color: theme.textSecondary, marginBottom: '0.5rem',
    }}>{label}</p>
    <p style={{
      fontSize: '1.7rem', fontWeight: 700, color: theme.textPrimary, lineHeight: 1,
    }}>{value}</p>
    {sub && (
      <p style={{ fontSize: '0.78rem', color: theme.textMuted, marginTop: '0.4rem', fontWeight: 500 }}>
        {sub}
      </p>
    )}
  </div>
);

const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  theme: ScorecardTheme;
}> = ({ title, subtitle, children, theme }) => (
  <div style={{
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    boxShadow: theme.cardShadow,
    borderRadius: 4,
    padding: '1rem 1.25rem 0.6rem',
  }}>
    <h3 style={{
      fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
      textTransform: 'uppercase', color: theme.sectionTitle,
      marginBottom: subtitle ? '0.15rem' : '0.75rem', paddingBottom: '0.5rem',
      borderBottom: `1px solid ${theme.sectionDivider}`,
    }}>{title}</h3>
    {subtitle && (
      <p style={{ fontSize: '0.7rem', color: theme.textMuted, marginBottom: '0.5rem' }}>
        {subtitle}
      </p>
    )}
    {children}
  </div>
);

/** A single person's row in the utilization table. */
const PersonRow: React.FC<{
  person: TeamCapacityPerson;
  theme: ScorecardTheme;
}> = ({ person, theme }) => {
  const barColor = utilizationColor(person.utilization, theme);
  // Cap the visible bar at 100% width; over-capacity is conveyed via color
  // + the numeric label rather than an overflowing bar.
  const barWidth = Math.min(person.utilization, 1) * 100;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1.4fr 1fr 2fr 0.7fr',
      gap: '0.75rem',
      alignItems: 'center',
      padding: '0.5rem 0',
      borderBottom: `1px solid ${theme.rowDivider}`,
      fontSize: '0.85rem',
    }}>
      <span style={{ color: theme.textPrimary, fontWeight: 500 }}>{person.name}</span>
      <span style={{ color: theme.textSecondary }}>
        {fmt(person.logged_hours, 1)} / {fmt(person.target_hours, 0)} h
      </span>
      <div style={{
        position: 'relative',
        height: 10,
        background: theme.rowDivider,
        borderRadius: 4,
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute',
          left: 0, top: 0, bottom: 0,
          width: `${barWidth}%`,
          background: barColor,
          borderRadius: 4,
          transition: 'width 0.3s ease',
        }} />
      </div>
      <span style={{ color: barColor, fontWeight: 700, textAlign: 'right' }}>
        {pct(person.utilization)}
      </span>
    </div>
  );
};

// ─── Shared toggle-pill style ─────────────────────────────────────────────

function pill(theme: ScorecardTheme, active: boolean): React.CSSProperties {
  return {
    fontSize: '0.78rem', fontWeight: 600, padding: '0.4rem 0.85rem',
    borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
    background: active ? theme.accent : 'transparent',
    color: active ? '#ffffff' : theme.textSecondary,
    border: `1px solid ${active ? theme.accent : theme.cardBorder}`,
  };
}

/**
 * Forecast colour scale — note this is the INVERSE intent of the past-view
 * utilizationColor: here high booked-load trends toward "full/over", low
 * means free capacity.
 *   >105% over capacity (red) · 85–105% near full (amber) · >0 has room (green)
 */
function forecastColor(util: number, theme: ScorecardTheme): string {
  if (util > 1.05) return theme.negative;
  if (util >= 0.85) return '#f59e0b';
  if (util > 0) return theme.positive;
  return theme.textMuted;
}

// ─── Upcoming forecast panel ──────────────────────────────────────────────

const ForecastPanel: React.FC<{ theme: ScorecardTheme }> = ({ theme }) => {
  const [source, setSource] = useState<ForecastSource>('task_load');
  const [granularity, setGranularity] = useState<ForecastGranularity>('day');
  const [data, setData] = useState<TeamCapacityForecast | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchTeamCapacityForecast({ source, granularity }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [source, granularity]);

  useEffect(() => { load(); }, [load]);

  const periods = data?.periods ?? [];
  const roster = data?.roster ?? [];

  const barSeries = useMemo(() => (data?.periods ?? []).map((p) => ({
    label: p.label,
    planned: p.team.planned_hours,
    capacity: p.team.capacity_hours,
    util: p.team.utilization,
  })), [data]);

  const cov = data?.coverage;
  const coverageNote = source === 'booked'
    ? (cov?.allocation_count
        ? `${cov.allocation_count} allocation(s) scheduled in this window.`
        : 'No allocations scheduled yet. As your team uses Teamwork’s Scheduler, booked time will appear here automatically.')
    : `Based on ${cov?.total_tasks ?? 0} upcoming task-days across ${cov?.people_with_data ?? 0}/${cov?.roster_size ?? 0} people with estimates. Tasks without estimates or due dates aren’t counted.`;

  const gridCols = `minmax(140px, 1.5fr) repeat(${periods.length || 1}, minmax(50px, 1fr))`;
  const isDay = granularity === 'day';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Controls */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '1rem', flexWrap: 'wrap',
      }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: theme.textPrimary, margin: 0 }}>
            Upcoming Capacity
          </h2>
          <p style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: '0.25rem' }}>
            Forward planned load vs capacity (35h/wk baseline).
            {data?.snapshot_at && (
              <> · updated {new Date(data.snapshot_at).toLocaleString()}</>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button type="button" style={pill(theme, source === 'task_load')} onClick={() => setSource('task_load')}>Task load</button>
            <button type="button" style={pill(theme, source === 'booked')} onClick={() => setSource('booked')}>Booked</button>
          </div>
          <span style={{ color: theme.rowDivider }}>|</span>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button type="button" style={pill(theme, isDay)} onClick={() => setGranularity('day')}>Daily · 4wk</button>
            <button type="button" style={pill(theme, !isDay)} onClick={() => setGranularity('week')}>Weekly · 12wk</button>
          </div>
          <button
            type="button" onClick={load} disabled={loading}
            style={{
              fontSize: '0.78rem', fontWeight: 600, padding: '0.4rem 0.85rem',
              borderRadius: 4, cursor: loading ? 'default' : 'pointer', fontFamily: 'inherit',
              background: 'transparent', color: theme.textSecondary,
              border: `1px solid ${theme.cardBorder}`, opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.negative}`,
          borderRadius: 4, padding: '1rem 1.25rem', color: theme.negative, fontSize: '0.85rem',
        }}>
          Failed to load forecast: {error}
        </div>
      )}

      {!error && (
        <>
          {/* Coverage / source note */}
          <div style={{
            fontSize: '0.78rem', color: theme.textSecondary, background: theme.cardBg,
            border: `1px solid ${theme.cardBorder}`, borderRadius: 4, padding: '0.6rem 0.85rem',
          }}>
            <strong style={{ color: theme.textPrimary }}>
              {source === 'booked' ? 'Booked (Scheduler): ' : 'Task load (Workload Planner): '}
            </strong>
            {coverageNote}
          </div>

          {/* Summary bars — team planned vs capacity */}
          <ChartCard
            title="Team planned vs capacity"
            subtitle={`Next ${periods.length} ${isDay ? 'weekdays' : 'weeks'}. Bars = planned hours; line = capacity.`}
            theme={theme}
          >
            {periods.length === 0 ? (
              <p style={{ color: theme.textMuted, fontSize: '0.85rem', padding: '0.75rem 0' }}>No upcoming periods.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={barSeries} margin={{ top: 8, right: 16, bottom: isDay ? 40 : 4, left: -8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.rowDivider} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: theme.textSecondary }}
                    interval={0}
                    angle={isDay ? -35 : 0}
                    textAnchor={isDay ? 'end' : 'middle'}
                    height={isDay ? 54 : 30}
                  />
                  <YAxis tick={{ fontSize: 12, fill: theme.textSecondary }} tickFormatter={(v) => `${v}h`} />
                  <Tooltip formatter={(v, n) => [`${(v as number).toFixed(1)} h`, n as string]} />
                  <Legend />
                  <Bar dataKey="planned" name="Planned" radius={[2, 2, 0, 0]}>
                    {barSeries.map((b, i) => (
                      <Cell key={i} fill={forecastColor(b.util, theme)} />
                    ))}
                  </Bar>
                  <Line type="monotone" dataKey="capacity" name="Capacity" stroke={theme.textSecondary} strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          {/* Per-person heatmap */}
          <ChartCard
            title="Per-person forecast"
            subtitle={`Planned hours per person per ${isDay ? 'day' : 'week'}. Green = has room · amber = near full · red = over capacity.`}
            theme={theme}
          >
            {roster.length === 0 || periods.length === 0 ? (
              <p style={{ color: theme.textMuted, fontSize: '0.85rem', padding: '0.75rem 0' }}>
                Nothing to forecast yet for this source.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '2px', alignItems: 'stretch' }}>
                  {/* Header row */}
                  <div style={{
                    position: 'sticky', left: 0, background: theme.cardBg, zIndex: 1,
                    fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: theme.textSecondary,
                    display: 'flex', alignItems: 'flex-end', padding: '0 0.4rem 0.35rem 0',
                  }}>Person</div>
                  {periods.map((p) => (
                    <div key={p.start} title={`${p.start} → ${p.end}`} style={{
                      fontSize: '0.6rem', fontWeight: 600, color: theme.textSecondary,
                      textAlign: 'center', padding: '0 0.1rem 0.35rem',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{p.label}</div>
                  ))}

                  {/* Body rows */}
                  {roster.map((person) => (
                    <React.Fragment key={person.user_id}>
                      <div style={{
                        position: 'sticky', left: 0, background: theme.cardBg, zIndex: 1,
                        fontSize: '0.8rem', color: theme.textPrimary, fontWeight: 500,
                        display: 'flex', alignItems: 'center', padding: '0.3rem 0.4rem 0.3rem 0',
                        whiteSpace: 'nowrap',
                      }}>{person.name}</div>
                      {periods.map((p) => {
                        const cell = p.by_person[person.user_id];
                        const planned = cell?.planned_hours ?? 0;
                        const util = cell?.utilization ?? 0;
                        const cap = cell?.capacity_hours ?? 0;
                        const has = planned > 0;
                        return (
                          <div
                            key={p.start}
                            title={`${person.name} — ${p.label}: ${planned}h planned / ${cap}h capacity (${Math.round(util * 100)}%)`}
                            style={{
                              background: has ? forecastColor(util, theme) : theme.rowDivider,
                              color: has ? '#ffffff' : theme.textMuted,
                              fontSize: '0.68rem', fontWeight: 700, textAlign: 'center',
                              borderRadius: 2, minHeight: '1.7rem',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >{has ? (planned < 10 ? planned.toFixed(1) : Math.round(planned)) : '·'}</div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            {/* Legend */}
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.75rem', fontSize: '0.72rem', color: theme.textSecondary }}>
              {[['Has room', theme.positive], ['Near full', '#f59e0b'], ['Over capacity', theme.negative], ['No load', theme.rowDivider]].map(([label, color]) => (
                <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                  <span style={{ width: 12, height: 12, borderRadius: 2, background: color as string, display: 'inline-block' }} />
                  {label}
                </span>
              ))}
            </div>
          </ChartCard>
        </>
      )}

      {loading && !data && (
        <p style={{ color: theme.textMuted, fontSize: '0.85rem' }}>Loading forecast…</p>
      )}
    </div>
  );
};

// ─── Main view ────────────────────────────────────────────────────────────

export const TeamCapacityView: React.FC<TeamCapacityProps> = ({ theme }) => {
  const [mainTab, setMainTab] = useState<'utilization' | 'upcoming'>('upcoming');
  const [view, setView] = useState<'weekly' | 'monthly'>('weekly');
  const [data, setData] = useState<TeamCapacityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const apiPeriod: 'week' | 'month' = view === 'weekly' ? 'week' : 'month';
  const count = view === 'monthly' ? 6 : 5;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchTeamCapacity({ period: apiPeriod, count });
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [apiPeriod, count]);

  useEffect(() => { load(); }, [load]);

  // periods come newest-first — periods[0] is the current window.
  const current = data?.periods[0] ?? null;
  const currentMetrics = current?.metrics ?? null;

  // Trend chart reads better oldest-first.
  const trendSeries = useMemo(() => {
    if (!data) return [];
    return [...data.periods].reverse().map((p) => ({
      label: shortLabel(p, apiPeriod),
      utilization: Math.round(p.metrics.team.utilization * 1000) / 10, // %
      billable: Math.round(p.metrics.team.billable_utilization * 1000) / 10,
    }));
  }, [data, apiPeriod]);

  const people = useMemo(
    () => [...(currentMetrics?.by_person ?? [])].sort((a, b) => b.utilization - a.utilization),
    [currentMetrics],
  );

  const tabStyle = (active: boolean): React.CSSProperties => ({
    fontSize: '0.78rem',
    fontWeight: 600,
    padding: '0.4rem 0.85rem',
    borderRadius: 4,
    cursor: 'pointer',
    fontFamily: 'inherit',
    background: active ? theme.accent : 'transparent',
    color: active ? '#ffffff' : theme.textSecondary,
    border: `1px solid ${active ? theme.accent : theme.cardBorder}`,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Section tabs: upcoming forecast (default) vs past utilization */}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button type="button" style={pill(theme, mainTab === 'upcoming')} onClick={() => setMainTab('upcoming')}>
          Upcoming
        </button>
        <button type="button" style={pill(theme, mainTab === 'utilization')} onClick={() => setMainTab('utilization')}>
          Utilization (past)
        </button>
      </div>

      {mainTab === 'upcoming' && <ForecastPanel theme={theme} />}

      {mainTab === 'utilization' && (
      <>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '1rem', flexWrap: 'wrap',
      }}>
        <div>
          <h2 style={{
            fontSize: '1.25rem', fontWeight: 700, color: theme.textPrimary, margin: 0,
          }}>
            Team Capacity
          </h2>
          <p style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: '0.25rem' }}>
            Logged hours vs available capacity, per person. Source: Teamwork
            {currentMetrics?.snapshot_at && (
              <> · updated {new Date(currentMetrics.snapshot_at).toLocaleString()}</>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button type="button" style={tabStyle(view === 'weekly')} onClick={() => setView('weekly')}>
            Weekly
          </button>
          <button type="button" style={tabStyle(view === 'monthly')} onClick={() => setView('monthly')}>
            Monthly
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            style={{
              fontSize: '0.78rem', fontWeight: 600, padding: '0.4rem 0.85rem',
              borderRadius: 4, cursor: loading ? 'default' : 'pointer', fontFamily: 'inherit',
              background: 'transparent', color: theme.textSecondary,
              border: `1px solid ${theme.cardBorder}`, opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.negative}`,
          borderRadius: 4, padding: '1rem 1.25rem', color: theme.negative, fontSize: '0.85rem',
        }}>
          Failed to load team capacity: {error}
        </div>
      )}

      {!error && currentMetrics && (
        <>
          {/* KPI cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '1rem',
          }}>
            <MetricCard
              label="Team members"
              value={fmt(currentMetrics.headcount)}
              sub={`${current?.label ?? ''}`}
              theme={theme}
            />
            <MetricCard
              label="Hours logged"
              value={`${fmt(currentMetrics.team.logged_hours, 0)} h`}
              sub={`of ${fmt(currentMetrics.team.target_hours, 0)} h capacity`}
              theme={theme}
            />
            <MetricCard
              label="Team utilization"
              value={pct(currentMetrics.team.utilization)}
              sub={`${currentMetrics.workdays} workdays × 7h × ${currentMetrics.headcount}`}
              theme={theme}
            />
            <MetricCard
              label="Billable utilization"
              value={pct(currentMetrics.team.billable_utilization)}
              sub={`${fmt(currentMetrics.team.billable_hours, 0)} h billable`}
              theme={theme}
            />
          </div>

          {/* Per-person breakdown */}
          <ChartCard
            title="Utilization by person"
            subtitle={`Capacity target: ${fmt(currentMetrics.per_person_target_hours, 0)} h per person this ${view === 'weekly' ? 'week' : 'month'} (${currentMetrics.workdays} workdays × 7h).`}
            theme={theme}
          >
            {people.length === 0 ? (
              <p style={{ color: theme.textMuted, fontSize: '0.85rem', padding: '0.75rem 0' }}>
                No time entries logged for this period.
              </p>
            ) : (
              <div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1.4fr 1fr 2fr 0.7fr',
                  gap: '0.75rem',
                  padding: '0.35rem 0',
                  borderBottom: `1px solid ${theme.sectionDivider}`,
                  fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.1em',
                  textTransform: 'uppercase', color: theme.textSecondary,
                }}>
                  <span>Person</span>
                  <span>Logged / Capacity</span>
                  <span>Utilization</span>
                  <span style={{ textAlign: 'right' }}>%</span>
                </div>
                {people.map((p) => (
                  <PersonRow key={p.user_id ?? p.name} person={p} theme={theme} />
                ))}
              </div>
            )}
          </ChartCard>

          {/* Trend */}
          <ChartCard
            title="Team utilization trend"
            subtitle={`Trailing ${data?.periods.length ?? 0} ${view === 'weekly' ? 'weeks' : 'months'}.`}
            theme={theme}
          >
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={trendSeries} margin={{ top: 8, right: 16, bottom: 4, left: -8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.rowDivider} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: theme.textSecondary }} />
                <YAxis
                  tick={{ fontSize: 12, fill: theme.textSecondary }}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip formatter={(v) => `${(v as number).toFixed(1)}%`} />
                <Line
                  type="monotone" dataKey="utilization" name="Utilization"
                  stroke={theme.accent} strokeWidth={2} dot={{ r: 3 }}
                />
                <Line
                  type="monotone" dataKey="billable" name="Billable"
                  stroke={theme.highlight ?? '#0ea5e9'} strokeWidth={2}
                  strokeDasharray="4 3" dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </>
      )}

      {!error && !currentMetrics && !loading && (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
          borderRadius: 4, padding: '2rem', textAlign: 'center', color: theme.textMuted,
        }}>
          No Teamwork data available yet.
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default TeamCapacityView;
